<template>
	<span class="close-sidebar" @click="closeSidebar" :style="{ zIndex: visible ? '1000' : '0' }">×</span>
	<div class="open-sidebar" v-if="visible">
		<h2 class="summary-title">{{ summary_title }}</h2>
		<!-- ファイル選択セクション -->
		<div class="file-select">
			<input type="file" @change="handleFileSelect" accept=".docx" />
		</div>
		<div class="button-container">
			<button class="button-style" @click="uploadSummary">{{ upload_button_name }}</button>
		</div>
		<div class="annotation-container">
			<p>{{ annotation }}</p>
		</div>
		<!-- プログレスバー -->
		<div class="progress-container">
			<div class="progress-bar" :class="{ 'is-active': progressPercentage > 0 }" :style="{ width: progressPercentage + '%' }">
				<span class="progress-text">{{ progressPercentage }}%</span>
			</div>
		</div>
		<div class="button-container">
			<button class="button-style" @click="downloadSummary" :disabled="progressPercentage < 100">{{ download_button_name }}</button>
		</div>

		<!-- サイドバーの内容を追加 -->
	</div>
</template>

<script>
import axios from "axios";
import { useBlobStore } from "@/stores/blobStore";
import api from "@/utils/api";

export default {
	name: "SummaryOutpur",
	props: {
		visible: {
			type: Boolean,
			required: true,
		},
		accessToken: String,
		userId: String,
		houseId: String,
	},
	emits: ["close"],
	data() {
		return {
			summary_title: "要約出力",
			upload_button_name: "OK",
			download_button_name: "要約ダウンロード",
			annotation: "※OKを押下すると要約処理が始まります。ダウンロードできるようになるには数秒～数分かかります。",
			progressPercentage: 0,
			summaryFile: null,
			downloadUrl: null,
			progressInterval: null,
		};
	},
	setup() {
		const blobStore = useBlobStore();
		return { blobStore };
	},

	methods: {
		handleFileSelect(event) {
			// 選択したファイルが正しく設定されているか確認
			if (event.target.files.length > 0) {
				this.summaryFile = event.target.files[0]; // 最初のファイルを取得
				// console.log("選択したファイル:", this.summaryFile); // デバッグ用
			} else {
				// alert("ファイルを選択してください。"); // ファイル未選択の場合のアラート
				this.summaryFile = null;
			}
		},

		closeSidebar() {
			this.$emit("close"); // サイドバーを閉じるためのイベント
		},

		async uploadSummary() {
			// console.log("要約を開始します");
			// ファイルが選択され低場合はアラートを出して処理を中断
			if (!this.summaryFile) {
				console.error("ファイルが選択されていません。要約を中止します。");
				alert("ファイルを選択してください。"); // ファイル未選択の場合のアラート
				return; // 処理を中断
			}

			// 拡張子チェック
			const fileExtension = this.summaryFile.name.split(".").pop().toLowerCase();
			if (fileExtension !== "docx") {
				console.error("ファイルの拡張子が「.docx」ではありません。要約を中止します。");
				alert("ファイルの拡張子が「.docx」以外のファイルはアップロードできません。");
				this.summaryFile = null;
				return;
			}

			// ストアにダウンロード用URLがあればクリア
			if (this.blobStore.summaryInfo.downloadUrl) {
				this.blobStore.clearSummaryInfo();
				this.downloadUrl = null;
			}

			// プログレスバーを初期化して開始
			this.progressPercentage = 0;
			this.startProgress();

			// フォームデータを作成
			const formData = new FormData();
			formData.append("file", this.summaryFile);
			formData.append("house_id", this.houseId);

			// URLにクエリパラメータを追加
			try {
				const response = await api.post("", formData, {
					params: {
						action: "PostTranscript",
					},
					timeout: 180000,
				});
				// console.log(response.data);
				this.blobStore.setSummaryInfo(response.data);
				this.downloadUrl = response.data.downloadUrl;
				console.log("要約が完了しました");
			} catch (error) {
				console.error("要約に失敗しました:", error);
				alert("要約に失敗しました。もう一度ファイルをアップロードしてください。");
				this.stopProgress(); // プログレスバーを停止
				// this.summaryFile = null;

				this.blobStore.clearSummaryInfo();
				this.downloadUrl = null;
			}
		},

		async downloadSummary() {
			const summaryUrl = this.blobStore.summaryInfo.downloadUrl;
			// console.log(summaryUrl);
			if (summaryUrl) {
				// ダウンロードするファイル名を指定
				const fileName = "指導記録(n).xlsx";

				// Blobデータを取得するためのリクエスト
				const downloadResponse = await axios.get(summaryUrl, {
					responseType: "blob",
				});

				// BlobデータをURLに変換し、ダウンロードリンクを作成
				const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
				const a = document.createElement("a");
				a.href = url;
				a.setAttribute("download", fileName);
				document.body.appendChild(a);
				a.click(); // ダウンロードをトリガー
				a.remove(); // リンクを削除
			} else {
				console.error("ダウンロード用のURLが設定されていません。");
			}
		},

		checkExpiry() {
			// ストアからサマリ情報を取得
			const summaryInfo = this.blobStore.summaryInfo;
			this.downloadUrl = summaryInfo.downloadUrl;
			const expiryTime = summaryInfo.expiryTime;
			// 現在時間を取得
			const now = new Date();
			// console.log("現在時刻：", now);
			// expiryTimeをDateオブジェクトに変換
			const expiryDate = new Date(expiryTime);
			// console.log("有効期限：", expiryDate);
			// ダウンロードURLと有効期限を確認し、どちらも有効の場合100にする
			if (this.downloadUrl && expiryDate > now) {
				this.progressPercentage = 100;
			} else {
				this.progressPercentage = 0;
				this.summaryFile = null;
				this.blobStore.clearSummaryInfo();
			}
		},

		startProgress() {
			this.progressPercentage = 0;
			this.progressInterval = setInterval(() => {
				// プログレスバーの進捗を管理
				if (this.downloadUrl) {
					// ダウンロード用URLが取得できたら進捗を100％にする
					this.progressPercentage = 100;
					clearInterval(this.progressInterval);
				} else if (this.progressPercentage < 99) {
					this.progressPercentage += 1;
				}
			}, 200);
		},

		stopProgress() {
			clearInterval(this.progressInterval);
			this.progressPercentage = 0;
		},
	},
};
</script>

<style>
.open-sidebar {
	position: absolute;
	top: 0;
	right: 0;
	width: 350px; /* サイドバーの幅 */
	height: 100%; /* 高さを100%に設定 */
	background-color: #fff; /* 背景色 */
	box-shadow: -2px 0 5px rgba(0, 0, 0, 0.5); /* 影を追加 */
	z-index: 999; /* 最高のZインデックスを設定 */
	box-sizing: border-box;
	transition: right 0.3s ease;
}

/* モーダルタイトルのスタイル */
.summary-title {
	background-color: rgb(136, 174, 82); /* タイトルの背景色 */
	padding: 15px; /* 上下左右にパディングを設定 */
	display: flex;
	justify-content: space-between;
	width: 100%;
	box-sizing: border-box;
}
.close-sidebar {
	position: absolute;
	top: 10px;
	right: 10px;
	cursor: pointer; /* マウスカーソルをポインターに設定 */
	font-weight: bold; /* 太字 */
}
/* ファイル選択のスタイル */
.file-select {
	margin: 30px; /* セクション全体にマージンを設定 */
}

/* ボタン全体のスタイル */
.button-container {
	display: flex;
	justify-content: center;
	align-items: center;
}
/* アップロード（OK）ボタンのスタイル */
.button-style {
	width: 200px; /* ボタンの幅を指定 */
	margin: 10px; /* ボタン間にスペースを設定 */
	padding: 10px 0; /* 縦方向のパディングを設定 */
	background-color: rgb(136, 174, 82); /* ボタンの背景色 */
	color: black; /* ボタンの文字色 */
	border: none; /* ボタンにボーダーを付けない */
	border-radius: 5px; /* ボタンの角を丸くする */
	cursor: pointer; /* マウスカーソルをポインターに設定 */
}

/* 注釈全体のスタイル */
.annotation-container {
	margin: 20px;
	font-size: small;
}

/* プログレスバーのスタイル */
.progress-container {
	width: 90%; /* プログレスバー全体を利用 */
	background-color: #f3f3f3; /* 背景色 */
	border-radius: 5px; /* 角を丸くする */
	border: solid 1px lightgray;
	margin: 20px; /* マージンを設定 */
	position: relative;
}

.progress-bar {
	height: 30px; /* プログレスバーの高さ */
	background-color: rgb(77, 175, 231); /* プログレスバーの色 */
	transition: width 0.5s; /* 幅の変更にアニメーションを追加 */
}

/* 非活性時のボタンのスタイル */
.button-style:disabled {
	background-color: #d3d3d3; /* グレーの背景色 */
	color: #a9a9a9; /* グレーの文字色 */
	cursor: not-allowed; /* マウスカーソルを禁止に設定 */
}

.progress-text {
	position: absolute; /* 絶対位置で配置 */
	top: 50%; /* 垂直方向の中央に配置 */
	left: 50%; /* 水平方向の中央に配置 */
	transform: translate(-50%, -50%); /* 真の中央に配置するため位置調整 */
	font-weight: bold; /* 太字 */
	color: black; /* テキストの色 */
	font-size: 16px;
	line-height: 30px; /* プログレスバーの高さと同じにする */
}
/* .progress-bar.is-active {
 
} */
</style>
